/**
 * Insert a symbol a keyboard does not offer.
 *
 * Worksheets are full of characters a teacher cannot type: the tick and cross a
 * pupil is asked to draw, the fractions and operators in a maths question, the
 * arrows in a matching exercise. Before this there was one button that inserted
 * one character (a tick), and everything else meant leaving the app to copy a
 * character from somewhere and paste it in.
 *
 * Grouped rather than listed, because "the tick" and "three quarters" are looked
 * for in different frames of mind, and a flat grid of forty glyphs is a search
 * task rather than a choice.
 *
 * The popover deliberately does not steal focus from the field: the insertion
 * point must survive the click, or the symbol lands somewhere the teacher did
 * not choose.
 */

import { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/ui'
import { Icon } from '../../components/icons'

type Group = { name: string; symbols: { char: string; name: string }[] }

/** Named in the words a teacher would use, so the tooltip is a help rather than
 *  a Unicode lookup. */
const GROUPS: Group[] = [
  {
    name: 'Marking',
    symbols: [
      { char: '✓', name: 'Tick' },
      { char: '✗', name: 'Cross' },
      { char: '○', name: 'Circle' },
      { char: '□', name: 'Box' },
      { char: '△', name: 'Triangle' },
      { char: '★', name: 'Star' },
      { char: '•', name: 'Bullet' },
      { char: '…', name: 'Dots' },
    ],
  },
  {
    name: 'Arrows',
    symbols: [
      { char: '→', name: 'Right arrow' },
      { char: '←', name: 'Left arrow' },
      { char: '↑', name: 'Up arrow' },
      { char: '↓', name: 'Down arrow' },
      { char: '↔', name: 'Both ways' },
      { char: '⟶', name: 'Long arrow' },
    ],
  },
  {
    name: 'Maths',
    symbols: [
      { char: '×', name: 'Multiply' },
      { char: '÷', name: 'Divide' },
      { char: '±', name: 'Plus or minus' },
      { char: '≈', name: 'About equal' },
      { char: '≠', name: 'Not equal' },
      { char: '≤', name: 'Less or equal' },
      { char: '≥', name: 'More or equal' },
      { char: '√', name: 'Square root' },
      { char: 'π', name: 'Pi' },
      { char: '°', name: 'Degrees' },
      { char: '²', name: 'Squared' },
      { char: '³', name: 'Cubed' },
    ],
  },
  {
    name: 'Fractions',
    symbols: [
      { char: '½', name: 'One half' },
      { char: '⅓', name: 'One third' },
      { char: '⅔', name: 'Two thirds' },
      { char: '¼', name: 'One quarter' },
      { char: '¾', name: 'Three quarters' },
      { char: '⅛', name: 'One eighth' },
    ],
  },
  {
    name: 'Money & marks',
    symbols: [
      { char: '₹', name: 'Rupee' },
      { char: '£', name: 'Pound' },
      { char: '€', name: 'Euro' },
      { char: '$', name: 'Dollar' },
      { char: '—', name: 'Long dash' },
      { char: '‘', name: 'Open quote' },
      { char: '’', name: 'Close quote' },
    ],
  },
]

export function SymbolPicker({
  disabled,
  onPick,
}: {
  disabled?: boolean
  onPick: (symbol: string) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Closing on an outside click or Escape is what makes this a popover rather
  // than a panel the teacher has to dismiss deliberately.
  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="tc-symbol-picker" ref={wrapRef}>
      <Button
        size="sm"
        variant="secondary"
        className="tc-symbol-trigger"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Insert a symbol"
        // The field must keep its cursor: without this the textarea loses focus
        // on mousedown and the symbol has nowhere to go.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((was) => !was)}
      >
        <Icon.symbols size={15} /> Symbols
      </Button>

      {open && (
        <div className="tc-symbol-pop" role="dialog" aria-label="Insert a symbol">
          {GROUPS.map((group) => (
            <section key={group.name}>
              <h4>{group.name}</h4>
              <div className="tc-symbol-grid">
                {group.symbols.map((symbol) => (
                  <button
                    key={symbol.char}
                    type="button"
                    className="tc-symbol-key"
                    title={symbol.name}
                    aria-label={symbol.name}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onPick(symbol.char)
                      setOpen(false)
                    }}
                  >
                    {symbol.char}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
